/**
 * `@react-native-google-signin/google-signin` → Google Identity Services (GIS) no navegador.
 *
 * O app só usa: configure, hasPlayServices, signIn, signInSilently, getTokens, getCurrentUser,
 * signOut (ver lib/googleDrive/googleAuth.ts). Aqui o "login" é um token de acesso OAuth com o
 * escopo pedido (drive.file), guardado na sessão da aba; o e-mail vem do próprio Drive
 * (`about.get`, que o escopo drive.file permite). Mesmo fluxo que o viewer usava em drive.ts.
 */
const GIS_SRC = 'https://accounts.google.com/gsi/client';
const TOKEN_KEY = 'kontto-web:gtoken';
const USER_KEY = 'kontto-web:guser';

interface TokenResponse { access_token?: string; expires_in?: number; error?: string; error_description?: string }
interface TokenClient { requestAccessToken(opts?: { prompt?: string }): void }
interface GisOAuth2 {
  initTokenClient(cfg: { client_id: string; scope: string; callback: (r: TokenResponse) => void; error_callback?: (e: { type?: string; message?: string }) => void }): TokenClient;
  revoke?(token: string, done?: () => void): void;
}
declare global { interface Window { google?: { accounts?: { oauth2?: GisOAuth2 } } } }

let clientId = '';
let scopes: string[] = [];
let gisLoading: Promise<GisOAuth2> | null = null;

function loadGis(): Promise<GisOAuth2> {
  if (window.google?.accounts?.oauth2) return Promise.resolve(window.google.accounts.oauth2);
  if (gisLoading) return gisLoading;
  gisLoading = new Promise<GisOAuth2>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = GIS_SRC;
    s.async = true;
    s.onload = () => {
      const api = window.google?.accounts?.oauth2;
      if (api) resolve(api); else reject(new Error('O Google não carregou.'));
    };
    s.onerror = () => { gisLoading = null; reject(new Error('Não deu pra carregar o Google. Confira a conexão e tente de novo.')); };
    document.head.appendChild(s);
  });
  return gisLoading;
}

interface Cached { token: string; expiresAt: number }
function readToken(): Cached | null {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    const t = raw ? (JSON.parse(raw) as Cached) : null;
    return t && t.expiresAt > Date.now() + 60_000 ? t : null;
  } catch { return null; }
}
function writeToken(t: Cached | null) {
  try { if (t) sessionStorage.setItem(TOKEN_KEY, JSON.stringify(t)); else sessionStorage.removeItem(TOKEN_KEY); } catch {}
}
function readUser(): { user: { email: string } } | null {
  try { const raw = localStorage.getItem(USER_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function writeUser(u: { user: { email: string } } | null) {
  try { if (u) localStorage.setItem(USER_KEY, JSON.stringify(u)); else localStorage.removeItem(USER_KEY); } catch {}
}

/** A autorização venceu e só um clique renova (o navegador bloqueia o popup do Google fora de gesto). */
export const RECONNECT_MESSAGE = 'A autorização do Google venceu. Clique em Reconectar para continuar sincronizando.';
export const POPUP_BLOCKED_MESSAGE = 'O navegador bloqueou a janela do Google. Permita pop-ups para este site e clique em Reconectar de novo.';
const RECONNECT_MESSAGES = [RECONNECT_MESSAGE, POPUP_BLOCKED_MESSAGE, 'A janela do Google foi fechada antes de terminar.', 'Falha no login do Google.'];
/** O erro (ou a mensagem guardada no store de sync) é daqueles que só um clique em Reconectar resolve? */
export function isReconnectError(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
  return RECONNECT_MESSAGES.some(m => msg.includes(m));
}
const reconnectError = (msg: string) => Object.assign(new Error(msg), { code: 'google_reconnect' });
/** Há token válido guardado nesta aba? Sem ele, qualquer falha do ciclo é "reconectar" — o motor de sync
 *  do app traduz o erro de autorização em outra mensagem, e aqui a causa é só o token vencido. */
export function hasGoogleToken(): boolean { return readToken() != null; }
/** Há um gesto do usuário ativo (clique recente)? Sem a API, deixa o navegador decidir. */
function userActivated(): boolean {
  const ua = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation;
  return ua ? ua.isActive : true;
}
let gestureDepth = 0;
/**
 * Roda `fn` como parte de um clique do usuário: só dentro disto a renovação do token pode abrir o
 * popup do Google. O ciclo automático que dispara 3 s depois de salvar ainda cairia na janela de
 * ativação do navegador (5 s) e abriria um popup do nada — por isso a decisão é de quem chama, não
 * só do navegador.
 */
export async function withUserGesture<T>(fn: () => Promise<T>): Promise<T> {
  gestureDepth += 1;
  try { return await fn(); } finally { gestureDepth -= 1; }
}

/**
 * Pede um token ao GIS. `prompt: ''` reaproveita a sessão Google viva — mas o GIS SEMPRE abre um popup,
 * e o navegador bloqueia popup sem gesto do usuário. Por isso a renovação automática (poll de 2 min,
 * envio pós-escrita) nem tenta: falha na hora com a mensagem de reconectar, sem disparar o aviso de
 * "pop-up bloqueado" do navegador e sem deixar a pessoa achando que sincronizou. Só o clique renova,
 * e o que foi escrito enquanto isso fica guardado na outbox/oplog até subir.
 */
async function requestToken(prompt: 'consent' | ''): Promise<string> {
  if (!clientId) throw new Error('Google Drive não configurado neste endereço (falta o client ID).');
  if (prompt === '' && (gestureDepth === 0 || !userActivated())) throw reconnectError(RECONNECT_MESSAGE);
  const gis = await loadGis();
  return new Promise<string>((resolve, reject) => {
    const client = gis.initTokenClient({
      client_id: clientId,
      scope: scopes.join(' '),
      callback: (r) => {
        if (r.error || !r.access_token) { reject(new Error(r.error_description ?? r.error ?? 'O Google não devolveu um token.')); return; }
        writeToken({ token: r.access_token, expiresAt: Date.now() + (r.expires_in ?? 3600) * 1000 });
        resolve(r.access_token);
      },
      error_callback: (e) => reject(reconnectError(e.type === 'popup_closed' ? 'A janela do Google foi fechada antes de terminar.' : e.type === 'popup_failed_to_open' ? POPUP_BLOCKED_MESSAGE : (e.message ?? 'Falha no login do Google.'))),
    });
    client.requestAccessToken({ prompt });
  });
}

async function emailFor(token: string): Promise<string> {
  const r = await fetch('https://www.googleapis.com/drive/v3/about?fields=user(emailAddress)', { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error(`Não foi possível ler a conta Google (${r.status}).`);
  const j = (await r.json()) as { user?: { emailAddress?: string } };
  if (!j.user?.emailAddress) throw new Error('Não foi possível ler a conta Google.');
  return j.user.emailAddress;
}

export const GoogleSignin = {
  configure(opts: { webClientId?: string; scopes?: string[] }) {
    clientId = (import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined)?.trim() || opts.webClientId || '';
    scopes = opts.scopes ?? [];
  },
  async hasPlayServices() { return true; },
  async signIn() {
    const token = await requestToken('consent');
    const email = await emailFor(token);
    const data = { user: { email } };
    writeUser(data);
    return { type: 'success', data };
  },
  async signInSilently() {
    const cached = readToken();
    const token = cached?.token ?? (await requestToken(''));
    const user = readUser() ?? { user: { email: await emailFor(token) } };
    writeUser(user);
    return { type: 'success', data: user };
  },
  async getTokens() {
    const cached = readToken();
    return { accessToken: cached?.token ?? (await requestToken('')), idToken: null };
  },
  getCurrentUser() { return readUser(); },
  // Sair só esquece o token daqui (ele vence sozinho em 1 h). NÃO chamar `google.accounts.oauth2.revoke`:
  // o revoke do GIS derruba a autorização da conta pro app Kontto inteiro, não só pra este navegador —
  // o celular perdia o login do Google e pedia conta e Drive de novo na abertura seguinte (15/10/2026).
  // Revogar de verdade é em myaccount.google.com/permissions, como a política de privacidade ensina.
  async signOut() {
    writeToken(null); writeUser(null);
  },
};

export default { GoogleSignin };
